/**
 * Capacity override helpers for the booking grids.
 *
 * A booking can carry a capacity override (`capacityParticipantCount` /
 * `capacityLaneCount`) that decouples the seats/lanes it blocks from the number
 * of guests actually attending. Typical case: a group plays among themselves and
 * therefore blocks a whole team pool although only a few guests are booked.
 *
 * The backend applies the same rules in `util/booking-handlers/booking-helpers.js`
 * when it calculates the capacity bar, so the grid overview has to use these
 * numbers as well — otherwise a blocked slot keeps rendering as available.
 */

const isUsableOverride = value =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0;

/**
 * Returns true when the booking blocks a different amount of capacity than its
 * participant count suggests.
 */
export const hasCapacityOverride = booking =>
  isUsableOverride(booking?.capacityParticipantCount) ||
  isUsableOverride(booking?.capacityLaneCount);

/**
 * Participant count to use for capacity/availability calculations.
 * Falls back to the real participant count when no override is set.
 */
export const getCapacityParticipantCount = booking => {
  if (isUsableOverride(booking?.capacityParticipantCount)) {
    return booking.capacityParticipantCount;
  }
  return Number(booking?.participantCount || 0);
};

/**
 * Lane count to use for capacity/availability calculations. Prefers the lane
 * override, then the stored lane count, then derives lanes from the (possibly
 * overridden) participant count via the entertainment's lane density.
 */
export const getCapacityLaneCount = (booking, participantLaneDensity) => {
  if (isUsableOverride(booking?.capacityLaneCount)) {
    return booking.capacityLaneCount;
  }
  if (isUsableOverride(booking?.laneCount)) {
    return booking.laneCount;
  }
  const participants = getCapacityParticipantCount(booking);
  const density = Number(participantLaneDensity);
  if (Number.isFinite(density) && density > 0) {
    return Math.ceil(participants / density);
  }
  return participants;
};

/**
 * Extra units a booking blocks on top of what its own occupancy shows. Used to
 * annotate the availability rows so staff can see why a slot has fewer free
 * places than the visible bookings suggest.
 */
export const getOverrideSurplus = (occupiedUnits, blockedUnits) => {
  const surplus = Number(blockedUnits || 0) - Number(occupiedUnits || 0);
  return surplus > 0 ? surplus : 0;
};
